import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  TrendingUp, 
  Users, 
  Calendar,
  DollarSign,
  ShoppingCart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminAnalytics = () => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [clientActivity, setClientActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendDays, setTrendDays] = useState("30");

  useEffect(() => {
    fetchData();
  }, [trendDays]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [overviewRes, trendsRes, clientRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, { headers }),
        axios.get(`${API}/admin/analytics/trends?days=${trendDays}`, { headers }),
        axios.get(`${API}/admin/analytics/client-activity`, { headers })
      ]);

      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setClientActivity(clientRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Analytics</h1>
          <p className="text-muted-foreground">Platform metrics and trends</p>
        </div>
        
        <Select value={trendDays} onValueChange={setTrendDays}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Time period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-400/10 rounded-sm">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">{overview?.photographers?.total || 0}</h3>
            <p className="text-sm text-muted-foreground">Total Photographers</p>
            <p className="text-xs text-green-400 mt-1">
              {overview?.photographers?.active || 0} active
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-400/10 rounded-sm">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">{overview?.bookings?.total || 0}</h3>
            <p className="text-sm text-muted-foreground">Total Bookings</p>
            <p className="text-xs text-yellow-400 mt-1">
              {overview?.bookings?.pending || 0} pending
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-400/10 rounded-sm">
                <ShoppingCart className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">{overview?.purchases?.total || 0}</h3>
            <p className="text-sm text-muted-foreground">Total Purchases</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-400/10 rounded-sm">
                <TrendingUp className="w-5 h-5 text-orange-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">
              {overview?.photographers?.trial_conversion_rate?.toFixed(1) || 0}%
            </h3>
            <p className="text-sm text-muted-foreground">Trial Conversion</p>
          </CardContent>
        </Card>
      </div>

      {/* Trends */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-serif">New Photographers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trends?.new_photographers?.length > 0 ? (
                trends.new_photographers.slice(-7).map((item, index) => (
                  <div key={item._id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{item._id}</span>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-serif">Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trends?.bookings?.length > 0 ? (
                trends.bookings.slice(-7).map((item, index) => (
                  <div key={item._id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{item._id}</span>
                    <span className="font-medium">{item.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-serif">Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trends?.revenue?.length > 0 ? (
                trends.revenue.slice(-7).map((item, index) => (
                  <div key={item._id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{item._id}</span>
                    <span className="font-medium">${item.total?.toFixed(2)}</span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-sm">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-serif">Recent Clients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {clientActivity?.recent_clients?.length > 0 ? (
                clientActivity.recent_clients.slice(0, 10).map((client, index) => (
                  <motion.div
                    key={client._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-sm"
                  >
                    <div>
                      <p className="font-medium">{client.name}</p>
                      <p className="text-sm text-muted-foreground">{client._id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{client.booking_count} bookings</p>
                      <p className="text-xs text-muted-foreground">
                        Last: {new Date(client.last_booking).toLocaleDateString()}
                      </p>
                    </div>
                  </motion.div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No client data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-serif">Top Buyers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {clientActivity?.top_buyers?.length > 0 ? (
                clientActivity.top_buyers.slice(0, 10).map((buyer, index) => (
                  <motion.div
                    key={buyer._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-sm"
                  >
                    <div>
                      <p className="font-medium">{buyer.name}</p>
                      <p className="text-sm text-muted-foreground">{buyer._id}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-400">${buyer.total_spent?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">
                        {buyer.purchase_count} purchases
                      </p>
                    </div>
                  </motion.div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No purchase data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Storage & Media Stats */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif">Platform Stats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-4 bg-muted/30 rounded-sm">
              <p className="text-sm text-muted-foreground">Total Galleries</p>
              <p className="text-2xl font-serif">{overview?.galleries?.total || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {overview?.galleries?.public || 0} public / {overview?.galleries?.private || 0} private
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm">
              <p className="text-sm text-muted-foreground">Media Assets</p>
              <p className="text-2xl font-serif">{overview?.media?.total_assets || 0}</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm">
              <p className="text-sm text-muted-foreground">Storage Used</p>
              <p className="text-2xl font-serif">{overview?.media?.total_storage_mb?.toFixed(1) || 0} MB</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-sm">
              <p className="text-sm text-muted-foreground">Confirmed Bookings</p>
              <p className="text-2xl font-serif">{overview?.bookings?.confirmed || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
